import { MenuItem } from "../data/menuData";
import { recipeBook, Recipe } from "../data/recipes";

/**
 * Interface for a menu item enriched with recipe data
 */
export interface MenuItemWithRecipe extends MenuItem {
  name: string;
  nameFr: string;
  nameKey: string;
  description: string;
  descriptionFr: string;
  descriptionKey: string;
  vegetarian?: boolean;
  vegan?: boolean;
  glutenFree?: boolean;
  spicy?: boolean;
}

/**
 * Merges a menu item with its corresponding recipe data from the Recipe Book
 * @param menuItem - The menu item to enrich
 * @returns The menu item with recipe data merged in
 */
export function getMenuItemWithRecipe(menuItem: MenuItem): MenuItemWithRecipe {
  // If the menu item has a recipeId, merge with recipe data
  if (menuItem.recipeId) {
    const recipe = recipeBook[menuItem.recipeId];

    if (!recipe) {
      console.warn(`Recipe not found for recipeId: ${menuItem.recipeId}`);
      // Return menu item as-is if recipe not found
      return menuItem as MenuItemWithRecipe;
    }

    // Merge recipe data with menu item, menu item fields take precedence
    return {
      ...menuItem,
      name: menuItem.name || recipe.name,
      nameFr: menuItem.nameFr || recipe.nameFr,
      nameKey: menuItem.nameKey || recipe.nameKey,
      description: menuItem.description || recipe.description,
      descriptionFr: menuItem.descriptionFr || recipe.descriptionFr,
      descriptionKey: menuItem.descriptionKey || recipe.descriptionKey,
      // Dietary flags from recipe (can be overridden by menu item)
      vegetarian: menuItem.vegetarian !== undefined ? menuItem.vegetarian : recipe.vegetarian,
      vegan: menuItem.vegan !== undefined ? menuItem.vegan : recipe.vegan,
      glutenFree: menuItem.glutenFree !== undefined ? menuItem.glutenFree : recipe.glutenFree,
      spicy: menuItem.spicy !== undefined ? menuItem.spicy : recipe.spicy,
    };
  }

  // If no recipeId, return the menu item as-is (it should have all fields already)
  return menuItem as MenuItemWithRecipe;
}

/**
 * Enriches an array of menu items with their recipe data
 * @param menuItems - Array of menu items to enrich
 * @returns Array of menu items with recipe data merged in
 */
export function getMenuItemsWithRecipes(menuItems: MenuItem[]): MenuItemWithRecipe[] {
  return menuItems.map(getMenuItemWithRecipe);
}

/**
 * Filters menu items and enriches them with recipe data
 * @param menuItems - Array of menu items
 * @param filterFn - Function to filter menu items
 * @returns Filtered and enriched menu items
 */
export function getFilteredMenuItemsWithRecipes(
  menuItems: MenuItem[],
  filterFn: (item: MenuItem) => boolean
): MenuItemWithRecipe[] {
  return menuItems.filter(filterFn).map(getMenuItemWithRecipe);
}

/**
 * Gets a recipe by ID from the Recipe Book
 * @param recipeId - The recipe ID to look up
 * @returns The recipe or undefined if not found
 */
export function getRecipe(recipeId: string): Recipe | undefined {
  return recipeBook[recipeId];
}
